import { useLoginStore } from "@/store/login";
import { useTokenStore } from "@/store/token";
import {
  checkBrowserSupport,
  supportedBrowsersMessage,
  type BrowserSupportResult,
} from "@/utils/browserSupport";
import {
  ExclamationCircleOutlined,
  LockOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Alert, Button, Card, Form, Input, Space, Typography } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useOutletContext } from "react-router";

type LoginValues = {
  account: string;
  password: string;
  captcha: string;
};

export default function LoginPage() {
  const { t } = useTranslation();
  const { isDark } = useOutletContext<{ isDark: boolean }>();
  const navigate = useNavigate();
  const { login, getCaptcha } = useLoginStore();
  const { isAuthenticated } = useTokenStore();
  const [form] = Form.useForm<LoginValues>();
  const [captchaImage, setCaptchaImage] = useState<string | undefined>(
    undefined,
  );
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [browserSupport, setBrowserSupport] =
    useState<BrowserSupportResult | null>(null);

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/", { replace: true });
    } else {
      checkBrowserSupport().then((result) => {
        setBrowserSupport(result);
        if (result.supported) {
          loadCaptcha().then(() => {});
        }
      });
    }
  }, [isAuthenticated, navigate]);

  const loadCaptcha = async () => {
    try {
      const data = await getCaptcha();
      setCaptchaImage(data);
      form.resetFields(["captcha"]);
    } catch (_) {
      setError(t("auth.captchaFail"));
    }
  };

  const handleSubmit = async (values: LoginValues) => {
    if (browserSupport?.supported === false) {
      setError(browserSupport.message || t("auth.browserUnsupported"));
      return;
    }
    setError("");
    setLoading(true);

    try {
      const isAuthenticated = await login({
        account: values.account,
        password: values.password,
        captcha: values.captcha,
      });
      if (isAuthenticated) {
        navigate("/");
      }
    } catch (err: any) {
      setError(err || t("auth.loginFail"));
      await loadCaptcha();
      form.resetFields(["captcha"]);
    } finally {
      setLoading(false);
    }
  };

  const cardClassName = isDark
    ? "w-full max-w-md rounded-3xl border border-white/10 bg-slate-900/80 text-white shadow-2xl backdrop-blur"
    : "w-full max-w-md rounded-3xl border border-slate-200/60 bg-white/80 text-slate-900 shadow-2xl backdrop-blur";
  const labelClassName = isDark ? "text-slate-200" : "text-slate-700";
  const hintClassName = isDark ? "text-slate-300" : "text-slate-500";
  function clearBrowserCache() {
    window.sessionStorage.clear();
    window.localStorage.clear();
  }
  if (browserSupport?.supported === false) {
    return (
      <div
        className={
          isDark
            ? "fixed inset-0 z-50 flex items-center justify-center bg-slate-950 px-6 text-white"
            : "fixed inset-0 z-50 flex items-center justify-center bg-slate-50 px-6 text-slate-950"
        }
      >
        <div className="max-w-2xl text-center">
          <ExclamationCircleOutlined className="mb-6 text-7xl text-red-500" />
          <Typography.Title level={1} className={isDark ? "text-white" : ""}>
            {t("auth.browserUnsupportedTitle")}
          </Typography.Title>
          <Typography.Paragraph
            className={
              isDark
                ? "mx-auto max-w-xl text-lg text-slate-200"
                : "mx-auto max-w-xl text-lg text-slate-700"
            }
          >
            {browserSupport.message || supportedBrowsersMessage}
          </Typography.Paragraph>
        </div>
      </div>
    );
  }

  return (
    <Card className={cardClassName}>
      <Space orientation="vertical" size="large" className="w-full">
        {error && <Alert type="error" showIcon title={error} />}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark={false}
        >
          <Form.Item
            label={
              <span className={labelClassName}>{t("auth.accountLabel")}</span>
            }
            name="account"
            rules={[{ required: true, message: t("auth.accountPlaceholder") }]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder={t("auth.accountPlaceholder")}
              size="large"
            />
          </Form.Item>

          <Form.Item
            label={
              <span className={labelClassName}>{t("auth.passwordLabel")}</span>
            }
            name="password"
            rules={[{ required: true, message: t("auth.passwordPlaceholder") }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder={t("auth.passwordPlaceholder")}
              size="large"
            />
          </Form.Item>

          <div className="flex items-center gap-2">
            <Form.Item
              label={
                <span className={labelClassName}>{t("auth.captchaLabel")}</span>
              }
              name="captcha"
              rules={[
                { required: true, message: t("auth.captchaPlaceholder") },
              ]}
            >
              <Input.OTP
                length={4}
                size="large"
                formatter={(str) => str.toUpperCase()}
              />
            </Form.Item>

            <img
              className="w-30 h-10 cursor-pointer"
              src={captchaImage}
              onClick={loadCaptcha}
              alt="captcha"
            />
          </div>

          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            block
            size="large"
          >
            {loading ? t("auth.signingIn") : t("auth.signIn")}
          </Button>
        </Form>

        <Typography.Text className={`text-center ${hintClassName}`}>
          {t("auth.noAccount")}{" "}
          <Link to="/register">{t("auth.createOne")}</Link>
          <Button
            type="link"
            className="ml-2 p-0"
            onClick={clearBrowserCache}
          >
            {t("auth.clearCache")}
          </Button>
        </Typography.Text>
      </Space>
    </Card>
  );
}
